import { useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { toast } from 'sonner';
import PageContentLoading from '../../../PageContentLoading';
import ModifyRecruitmentStepper from '../Components/ModifyRecruitmentStepper';
import { CreateFullRecruitmentStepMapper } from '../Components/StepMappers';
import useOneClickCreateRecruitment, { OneClickCreateRecruitmentResponse } from './api/useOneClickCreateRecruitment';
import OneClickFallbackDialog from './Components/OneClickFallbackDialog';
import OneClickRecruitmentForm from './Components/OneClickRecruitmentForm';
import { useCreateRecruitmentContext } from './Contexts/CreateRecruitmentContext';
import { useJobFunctions } from '@/api/useJobFunctions';
import TopBar from '@/Components/TopBars/TopBar';
import useClientIdPathParam from '@/Hooks/UseClientIdPathParam';
import useCompanyId from '@/Hooks/UseCompanyId';
import ProcessingState from '@/Models/ProcessingState';
import Page from '@/Pages/Page';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

// Which creation path the recruiter is on. Everyone starts on the one-click entry
// screen and can skip straight to the unchanged manual stepper at any time.
type CreationMode = 'oneClick' | 'manual';

const CreateRecruitmentPageContent = () => {
    const { processingState, gettingDataProcessingState, applyOneClickPrefill } = useCreateRecruitmentContext();
    const { isLoading: isJobFunctionsLoading } = useJobFunctions();

    const navigate = useNavigate();
    const companyId = useCompanyId();
    const clientId = useClientIdPathParam();

    const [mode, setMode] = useState<CreationMode>('oneClick');
    const { trigger: validateOneClick, isMutating: isSubmittingOneClick } = useOneClickCreateRecruitment(clientId);
    const [oneClickError, setOneClickError] = useState<string | undefined>(undefined);
    const [fallbackResult, setFallbackResult] = useState<OneClickCreateRecruitmentResponse | null>(null);

    const pageContentContainerRef = useRef<HTMLDivElement>(null);
    const isLoading = gettingDataProcessingState === ProcessingState.Processing || isJobFunctionsLoading;
    const steps = [
        UIStrings.JobDetails,
        UIStrings.JobDescription,
        UIStrings.SkillsTests,
        UIStrings.ApplicantInformation,
        UIStrings.IdentifyExperience,
    ];

    const handleOneClickSubmit = async (url: string) => {
        setOneClickError(undefined);
        try {
            const result = await validateOneClick({ url });

            if (result?.outcome === 'Created') {
                // Same destination as the end of the manual flow.
                navigate(generatePath(RoutePath.AssessmentsActive, { companyId, clientId }));
                toast.success(UIStrings.RecruitmentHasBeenCreated(result.recruitment!.jobDetails.name));
                return;
            }

            if (result?.outcome === 'Fallback') {
                // Partial/invalid extraction: explain via a dialog and let the recruiter decide whether to
                // continue to the (pre-filled) manual wizard or stay on the entry screen.
                setFallbackResult(result);
            }
        } catch {
            // A hard failure comes back as HTTP 502 (the request rejects): show an inline error.
            setOneClickError(UIStrings.OneClickHardFailureError);
        }
    };

    const handleFallbackContinue = async () => {
        // Pre-fill the manual creation wizard with extracted data then land the recruiter on step 1.
        if (fallbackResult?.prefill) {
            await applyOneClickPrefill(fallbackResult.prefill);
        }
        setFallbackResult(null);
        setMode('manual');
    };

    // Dismissing the dialog keeps the recruiter on the one-click entry screen.
    const handleFallbackDismiss = () => setFallbackResult(null);

    return (
        <Page
            topBar={<TopBar onlyLogo />}
            pageContainerProps={{ className: 'h-screen' }}
            pageContentContainerProps={{
                className: 'relative grow',
                style: {},
                ref: pageContentContainerRef,
            }}
        >
            <PageContentLoading
                isLoading={isLoading}
                loadingFailed={
                    processingState === ProcessingState.Error || gettingDataProcessingState === ProcessingState.Error
                }
            >
                {mode === 'oneClick' ? (
                    <OneClickRecruitmentForm
                        onSubmit={handleOneClickSubmit}
                        onSkip={() => setMode('manual')}
                        isSubmitting={isSubmittingOneClick}
                        errorMessage={oneClickError}
                    />
                ) : (
                    <ModifyRecruitmentStepper
                        steps={steps}
                        stepMapper={CreateFullRecruitmentStepMapper}
                        containerRef={pageContentContainerRef}
                    />
                )}

                <OneClickFallbackDialog
                    open={fallbackResult !== null}
                    onContinue={handleFallbackContinue}
                    onDismiss={handleFallbackDismiss}
                />
            </PageContentLoading>
        </Page>
    );
};

export default CreateRecruitmentPageContent;
